import { HttpContext } from '@angular/common/http';
import { Injectable, OnDestroy, inject } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Subscription, combineLatest, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Address, Bundle, BundleEntry, ContactPoint, Patient, Appointment } from 'fhir/r4';
import { authBackendEndPointToken, backendEndPointToken } from '../app.config';
import { DisableCountParam, LoadingUIEnabled } from '../loading.interceptor';
import { TableFilters, TableFilteringService, TableRowModel } from '../shared/table-filtering.service';

export interface PatientRow extends TableRowModel {
    rawResource: Patient;
    patient: Patient;
    name?: Patient['name'];
    telecom?: Patient['telecom'];
    address?: Patient['address'];
    gender?: Patient['gender'];
    birthDate?: Patient['birthDate'];
    identifier?: Patient['identifier'];
    primaryIdentifier?: string | null;
    primaryPhone?: string | null;
    primaryEmail?: string | null;
    addressLabel?: string | null;
    appointmentTime?: string;
    appointmentId?: string;
    triageLevel?: string;
}

export interface PatientSearchInitOptions {
    excludePharmacyWalkIns?: boolean;
    pharmacyWalkInTagQuery?: string;
    pageSize?: number;
    maxCacheRows?: number;
    practitionerId?: string;
}

@Injectable({ providedIn: 'root' })
export class PatientSearchService implements OnDestroy {
    private readonly tableFiltering = inject<TableFilteringService<Patient, PatientRow>>(TableFilteringService);
    private readonly backendUrl = inject(backendEndPointToken);
    private readonly http = inject(HttpClient);
    private readonly silentHttpContext = new HttpContext()
        .set(LoadingUIEnabled, false)
        .set(DisableCountParam, true);

    private readonly dummyDataSource = new MatTableDataSource<PatientRow>();
    private readonly internalDataSource = new MatTableDataSource<PatientRow>();
    private readonly pinnedRowsSubject = new BehaviorSubject<PatientRow[]>([]);
    private combineSub?: Subscription;

    private excludeWalkIns = true;
    private pharmacyTagQuery: string | undefined;
    private pageSize = 25;
    private maxCacheRows = 200;

    readonly loading$ = this.tableFiltering.loading$;
    readonly total$ = this.tableFiltering.total$;
    readonly displayedTotal$ = this.tableFiltering.displayedTotal$;
    readonly unknownTotal$ = this.tableFiltering.unknownTotal$;
    readonly error$ = this.tableFiltering.error$;

    init(options: PatientSearchInitOptions = {}): void {
        this.excludeWalkIns = options.excludePharmacyWalkIns ?? true;
        this.pharmacyTagQuery = options.pharmacyWalkInTagQuery;
        this.pageSize = options.pageSize ?? this.pageSize;
        this.maxCacheRows = options.maxCacheRows ?? this.maxCacheRows;

        this.pinnedRowsSubject.next([]);

        this.combineSub?.unsubscribe();
        this.combineSub = combineLatest([
            this.dummyDataSource.connect(),
            this.pinnedRowsSubject
        ]).subscribe(([paginatedRows, pinnedRows]) => {
            if (!paginatedRows && !pinnedRows) return;
            const safePaginated = paginatedRows || [];
            const safePinned = pinnedRows || [];
            const pinnedIds = new Set(safePinned.map(r => r.id));
            const filteredPaginated = safePaginated.filter(r => !pinnedIds.has(r.id));
            
            // Only prepend pinned rows if we are on the first page of the dummy source
            // Actually, to make them always visible at the top, we just prepend them
            this.internalDataSource.data = [...safePinned, ...filteredPaginated];
        });

        if (options.practitionerId) {
            this.loadPinnedAppointments(options.practitionerId);
        }

        this.tableFiltering.init({
            resourceType: 'Patient',
            defaultQuery: this.buildDefaultQuery(),
            dataSource: this.dummyDataSource,
            baseUrl: this.backendUrl,
            pageSize: this.pageSize,
            maxCacheRows: this.maxCacheRows,
            transformEntry: (resource, entry) => this.transformPatient(resource, entry),
            httpOptions: {
                context: this.silentHttpContext
            }
        });
    }

    get dataSource(): MatTableDataSource<PatientRow> {
        return this.internalDataSource;
    }

    search(filters: TableFilters): void {
        this.tableFiltering.search(filters);
    }

    changePage(pageIndex: number): void {
        this.tableFiltering.changePage(pageIndex);
    }

    refresh(): void {
        this.tableFiltering.refreshCurrentPage();
    }

    destroy(): void {
        this.tableFiltering.destroy();
        this.combineSub?.unsubscribe();
        this.dummyDataSource.disconnect();
        this.dummyDataSource.data = [];
        this.internalDataSource.disconnect();
        this.internalDataSource.data = [];
        this.pinnedRowsSubject.next([]);
    }

    ngOnDestroy(): void {
        this.destroy();
    }

    private buildDefaultQuery(): string {
        const params = new URLSearchParams();
        params.set('active', 'true');
        params.set('deceased:not', 'true');
        params.set('_count', String(this.pageSize));

        if (this.excludeWalkIns && this.pharmacyTagQuery) {
            params.set('_tag:not', this.pharmacyTagQuery);
        }

        return `/Patient?${params.toString()}`;
    }

    private loadPinnedAppointments(practitionerId: string): void {
        const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
        // Filter out fulfilled, cancelled, noshow, arrived? Usually just not fulfilled and cancelled.
        const query = `/Appointment?actor=Practitioner/${practitionerId}&date=ge${today}T00:00:00&date=le${today}T23:59:59&status:not=fulfilled&status:not=cancelled&status:not=noshow&_sort=date`;
        
        this.http.get<Bundle<Appointment>>(`${this.backendUrl}${query}`, { context: this.silentHttpContext })
            .pipe(
                map(bundle => (bundle.entry ?? []).map(e => e.resource as Appointment).filter(Boolean)),
                switchMap(appointments => {
                    if (appointments.length === 0) return of([]);
                    
                    const patientMap = new Map<string, { appt: Appointment, triageLevel?: string }>();
                    appointments.forEach(appt => {
                        const patientParticipant = appt.participant?.find(p => p.actor?.reference?.startsWith('Patient/'));
                        if (patientParticipant?.actor?.reference) {
                            const pId = patientParticipant.actor.reference.replace('Patient/', '');
                            // Extract triage level from extension
                            const triageExt = appt.extension?.find(e => e.url === 'http://e-likita.org/fhir/StructureDefinition/triage-level');
                            const triageLevel = triageExt?.valueString || triageExt?.valueCode;
                            if (!patientMap.has(pId)) {
                                patientMap.set(pId, { appt, triageLevel });
                            }
                        }
                    });
                    
                    const pIds = Array.from(patientMap.keys());
                    if (pIds.length === 0) return of([]);

                    // Fetch patients
                    const patientQuery = `/Patient?_id=${pIds.join(',')}`;
                    return this.http.get<Bundle<Patient>>(`${this.backendUrl}${patientQuery}`, { context: this.silentHttpContext }).pipe(
                        map(bundle => {
                            const patients = (bundle.entry ?? []).map(e => e.resource as Patient).filter(Boolean);
                            const rows: PatientRow[] = [];
                            
                            // To maintain appointment order, we iterate through the appointments again
                            appointments.forEach(appt => {
                                const patientParticipant = appt.participant?.find(p => p.actor?.reference?.startsWith('Patient/'));
                                if (patientParticipant?.actor?.reference) {
                                    const pId = patientParticipant.actor.reference.replace('Patient/', '');
                                    const patient = patients.find(p => p.id === pId);
                                    if (patient && !rows.some(r => r.id === pId)) {
                                        const triageExt = appt.extension?.find(e => e.url === 'http://e-likita.org/fhir/StructureDefinition/triage-level');
                                        const triageLevel = triageExt?.valueString || triageExt?.valueCode;
                                        const row = this.transformPatient(patient);
                                        row.appointmentTime = appt.start;
                                        row.appointmentId = appt.id;
                                        row.triageLevel = triageLevel;
                                        rows.push(row);
                                    }
                                }
                            });
                            
                            return rows;
                        })
                    );
                }),
                catchError(err => {
                    console.error('Failed to load pinned appointments for triage:', err);
                    return of([]);
                })
            )
            .subscribe(rows => {
                this.pinnedRowsSubject.next(rows);
            });
    }

    private transformPatient(resource: Patient, entry?: BundleEntry<Patient>): PatientRow {
        const fallbackId = resource.id ?? entry?.resource?.id ?? entry?.fullUrl ?? `Patient-${Date.now()}`;
        const primaryIdentifier = resource.identifier?.find((identifier) => !!identifier?.value)?.value ?? null;

        return {
            id: fallbackId,
            resourceType: resource.resourceType ?? 'Patient',
            display: this.buildName(resource) ?? fallbackId,
            rawResource: resource,
            patient: resource,
            name: resource.name,
            telecom: resource.telecom,
            address: resource.address,
            gender: resource.gender,
            birthDate: resource.birthDate,
            identifier: resource.identifier,
            primaryIdentifier,
            primaryPhone: this.extractContact(resource.telecom, 'phone'),
            primaryEmail: this.extractContact(resource.telecom, 'email'),
            addressLabel: this.buildAddress(resource.address)
        };
    }

    private buildName(resource: Patient): string | null {
        const preferredName = resource.name?.find((name) => name.use === 'official' || name.use === 'usual') ?? resource.name?.[0];
        if (!preferredName) {
            return null;
        }
        const given = preferredName.given?.join(' ') ?? '';
        const family = preferredName.family ?? '';
        return [given, family].filter(Boolean).join(' ').trim() || null;
    }

    private extractContact(telecom: ContactPoint[] | undefined, system: string): string | null {
        if (!telecom?.length) {
            return null;
        }
        return telecom.find((contact) => contact.system === system)?.value ?? null;
    }

    private buildAddress(addresses: Address[] | undefined): string | null {
        if (!addresses?.length) {
            return null;
        }
        const preferred = addresses.find((addr) => addr.use === 'home') ?? addresses[0];
        const parts = [preferred.line?.join(' '), preferred.city, preferred.state, preferred.country];
        const formatted = parts.filter(Boolean).join(', ').trim();
        return formatted || null;
    }
}
